import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import API_ENDPOINTS from '../ApiEndPoints';

const EMPTY_CONDITION = {
  condition: 'and',
  field: '',
  operator: 'equal',
  value: '',
  field_index: null,
};

const WorkflowFilterEditor = ({
  open,
  workflowId,
  selectedFilterTid,
  onPersistFilterSelection,
  persistingFilter,
  onInfo,
  onError,
  onRefreshBoard,
}) => {
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState([]);
  const [fields, setFields] = useState([]);
  const [operators, setOperators] = useState([]);
  const [operatorLabelMap, setOperatorLabelMap] = useState({});
  const [conditions, setConditions] = useState([]);
  const [conditionDraft, setConditionDraft] = useState(EMPTY_CONDITION);
  const [editingConditionIndex, setEditingConditionIndex] = useState(null);
  const [editorFilter, setEditorFilter] = useState(null);
  const [editorFilterTid, setEditorFilterTid] = useState(null);
  const [defaultFilterTid, setDefaultFilterTid] = useState(null);
  const [conditionOptions, setConditionOptions] = useState(['and', 'or']);
  const [savingFilter, setSavingFilter] = useState(false);
  const [deletingFilter, setDeletingFilter] = useState(false);

  const fieldMap = useMemo(() => {
    const map = {};
    fields.forEach((field) => {
      if (field?.name) {
        map[field.name] = field;
      }
    });
    return map;
  }, [fields]);

  const loadFilterData = useCallback(async (filterTid = null) => {
    if (!workflowId || !open) {
      return;
    }
    setLoading(true);
    try {
      const params = {};
      if (filterTid) {
        params.filter_tid = filterTid;
      }
      const { data } = await axios.get(API_ENDPOINTS.workflowBoard.filterEditor(workflowId), { params });
      setFilters(data.filters || []);
      setFields(data.fields || []);
      setOperators(data.operators || []);
      const operatorLookup = {};
      (data.operators || []).forEach((entry) => {
        operatorLookup[entry.value] = entry.label;
      });
      setOperatorLabelMap(operatorLookup);
      setConditionOptions(data.conditions || ['and', 'or']);
      setDefaultFilterTid(data.default_filter_tid ?? null);

      if (data.filter) {
        setEditorFilter(data.filter);
        setEditorFilterTid(data.filter.tid);
        setConditions(Array.isArray(data.filter.conditions) ? data.filter.conditions : []);
      } else {
        setEditorFilter(null);
        setEditorFilterTid(null);
        setConditions([]);
      }
      setConditionDraft(EMPTY_CONDITION);
      setEditingConditionIndex(null);
    } catch (error) {
      onError?.(error?.response?.data?.message || 'Unable to load filters.');
    } finally {
      setLoading(false);
    }
  }, [workflowId, open, onError]);

  useEffect(() => {
    if (open) {
      loadFilterData();
    }
  }, [open, loadFilterData]);

  const isEditingExisting = Boolean(editorFilterTid);

  const startNewFilter = () => {
    setEditorFilter({ title: '', description: '' });
    setEditorFilterTid(null);
    setConditions([]);
    setConditionDraft(EMPTY_CONDITION);
    setEditingConditionIndex(null);
  };

  const handleSelectFilter = (event) => {
    const tid = event.target.value || null;
    if (tid) {
      loadFilterData(tid);
    } else {
      setEditorFilter(null);
      setEditorFilterTid(null);
      setConditions([]);
    }
  };

  const updateConditionDraft = (patch) => {
    setConditionDraft((prev) => ({
      ...prev,
      ...patch,
    }));
  };

  const selectedField = conditionDraft.field ? fieldMap[conditionDraft.field] : null;
  const draftHasOptions = selectedField && Array.isArray(selectedField.values) && selectedField.values.length > 0;

  const resetConditionDraft = () => {
    setConditionDraft(EMPTY_CONDITION);
    setEditingConditionIndex(null);
  };

  const addOrUpdateCondition = () => {
    if (!conditionDraft.field || !conditionDraft.operator) {
      onError?.('Select a field and operator before adding a condition.');
      return;
    }

    if (!draftHasOptions && (conditionDraft.value === undefined || conditionDraft.value === null || conditionDraft.value === '')) {
      onError?.('Enter a value for this condition.');
      return;
    }

    const normalized = {
      condition: conditions.length === 0 ? 'and' : (conditionDraft.condition || 'and'),
      field: conditionDraft.field,
      operator: conditionDraft.operator,
      value: draftHasOptions ? conditionDraft.value || '' : conditionDraft.value,
      field_index: conditionDraft.field_index ?? (selectedField ? selectedField.field_index : null),
    };

    setConditions((prev) => {
      const next = [...prev];
      if (editingConditionIndex !== null && typeof editingConditionIndex === 'number') {
        next[editingConditionIndex] = normalized;
      } else {
        next.push(normalized);
      }
      return next;
    });

    resetConditionDraft();
  };

  const handleEditCondition = (index) => {
    const target = conditions[index];
    if (!target) {
      return;
    }
    setConditionDraft({ ...target });
    setEditingConditionIndex(index);
  };

  const handleDeleteCondition = (index) => {
    setConditions((prev) => prev.filter((_, idx) => idx !== index));
    if (editingConditionIndex === index) {
      resetConditionDraft();
    }
  };

  const handleSaveFilter = async () => {
    if (!editorFilter?.title) {
      onError?.('Give this filter a name before saving.');
      return;
    }
    if (!conditions.length) {
      onError?.('Add at least one condition before saving.');
      return;
    }

    setSavingFilter(true);
    try {
      const payload = {
        title: editorFilter.title,
        description: editorFilter.description,
        conditions,
      };

      let nextTid = editorFilterTid;

      if (isEditingExisting) {
        const { data } = await axios.put(
          API_ENDPOINTS.workflowBoard.updateFilter(workflowId, editorFilterTid),
          payload,
        );
        if (data?.filters) {
          setFilters(data.filters);
        }
      } else {
        const { data } = await axios.post(
          API_ENDPOINTS.workflowBoard.createFilter(workflowId),
          payload,
        );
        if (data?.filter) {
          setEditorFilter(data.filter);
          setEditorFilterTid(data.filter.tid);
          nextTid = data.filter.tid;
        }
        if (data?.filters) {
          setFilters(data.filters);
        }
      }

      onInfo?.('Filter saved.');
      await loadFilterData(nextTid);
      onRefreshBoard?.();
    } catch (error) {
      onError?.(error?.response?.data?.message || 'Unable to save filter.');
    } finally {
      setSavingFilter(false);
    }
  };

  const handleDeleteFilter = async () => {
    if (!isEditingExisting || !editorFilterTid) {
      return;
    }
    const confirmed = window.confirm('Delete this filter? This cannot be undone.');
    if (!confirmed) {
      return;
    }
    setDeletingFilter(true);
    try {
      const { data } = await axios.delete(
        API_ENDPOINTS.workflowBoard.deleteFilter(workflowId, editorFilterTid),
      );
      if (data?.filters) {
        setFilters(data.filters);
      }
      setEditorFilter(null);
      setEditorFilterTid(null);
      setConditions([]);
      setDefaultFilterTid(data?.default_filter_tid ?? defaultFilterTid);
      onInfo?.('Filter deleted.');
      onRefreshBoard?.();
    } catch (error) {
      onError?.(error?.response?.data?.message || 'Unable to delete filter.');
    } finally {
      setDeletingFilter(false);
    }
  };

  const handleSetDefault = async (tid) => {
    if (!tid || !onPersistFilterSelection) {
      return;
    }
    try {
      await onPersistFilterSelection(tid, { showToast: false });
      setDefaultFilterTid(tid);
      onInfo?.('Filter selected.');
    } catch (error) {
      onError?.(error?.response?.data?.message || 'Unable to set default filter.');
    }
  };

  const handleClearDefault = async () => {
    if (!onPersistFilterSelection) {
      return;
    }
    try {
      await onPersistFilterSelection(null, { showToast: false });
      setDefaultFilterTid(null);
      onInfo?.('Default filter cleared.');
    } catch (error) {
      onError?.(error?.response?.data?.message || 'Unable to clear default filter.');
    }
  };

  const renderConditionValueInput = () => {
    if (!selectedField) {
      return (
        <input
          type="text"
          className="form-control input-sm"
          value={conditionDraft.value || ''}
          onChange={(event) => updateConditionDraft({ value: event.target.value })}
        />
      );
    }

    if (draftHasOptions) {
      return (
        <select
          className="form-control input-sm"
          value={conditionDraft.value || ''}
          onChange={(event) => updateConditionDraft({ value: event.target.value })}
        >
          <option value="">Select value…</option>
          {selectedField.values.map((option) => (
            <option key={option.value || option.label} value={option.value}>
              {option.label || option.value}
            </option>
          ))}
        </select>
      );
    }

    const inputType = selectedField.type === 'number' ? 'number' : 'text';

    return (
      <input
        type={inputType}
        className="form-control input-sm"
        value={conditionDraft.value || ''}
        onChange={(event) => updateConditionDraft({ value: event.target.value })}
      />
    );
  };

  return (
    <section className="workflow-settings-section">
      <div className="workflow-settings-section__header">
        <h5>Filters</h5>
        <p>Create reusable filters to narrow the workflow board.</p>
      </div>

      {loading && <div className="alert alert-info">Loading filter details…</div>}

      <div className="workflow-filter-editor">
        <div className="workflow-filter-editor__toolbar">
          <label className="workflow-filter-editor__label">Existing filters</label>
          <div className="workflow-filter-editor__selector">
            <select className="form-control input-sm" onChange={handleSelectFilter} value={editorFilterTid || ''}>
              <option value="">Select a filter…</option>
              {filters.map((filter) => (
                <option key={filter.tid} value={filter.tid}>
                  {filter.title}
                </option>
              ))}
            </select>
            <button type="button" className="btn btn-link btn-xs" onClick={startNewFilter}>
              New filter
            </button>
          </div>
          <div className="workflow-filter-editor__default-actions">
            <button
              type="button"
              className="btn btn-xs btn-default"
              onClick={() => handleSetDefault(editorFilterTid || selectedFilterTid)}
              disabled={persistingFilter || (!editorFilterTid && !selectedFilterTid)}
            >
              Set as default
            </button>
            <button
              type="button"
              className="btn btn-xs btn-link"
              onClick={handleClearDefault}
              disabled={persistingFilter || !defaultFilterTid}
            >
              Clear default
            </button>
          </div>
          {defaultFilterTid && (
            <p className="workflow-filter-editor__meta">
              Default filter: <strong>{defaultFilterTid}</strong>
            </p>
          )}
        </div>

        {editorFilter && (
          <div className="workflow-filter-editor__form">
            <div className="workflow-filter-editor__row">
              <label>Name</label>
              <input
                type="text"
                className="form-control input-sm"
                value={editorFilter.title || ''}
                onChange={(event) => setEditorFilter((prev) => ({ ...prev, title: event.target.value }))}
              />
              {editorFilterTid && (
                <span className="workflow-filter-editor__tid">TID: {editorFilterTid}</span>
              )}
            </div>
            <div className="workflow-filter-editor__actions">
              <div>
                {isEditingExisting && (
                  <button
                    type="button"
                    className="btn btn-danger btn-xs"
                    onClick={handleDeleteFilter}
                    disabled={deletingFilter}
                  >
                    {deletingFilter ? 'Deleting…' : 'Delete'}
                  </button>
                )}
              </div>
              <div>
                <button
                  type="button"
                  className="btn btn-link btn-xs"
                  onClick={() => loadFilterData(editorFilterTid)}
                  disabled={loading}
                >
                  Reset
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-xs"
                  onClick={handleSaveFilter}
                  disabled={savingFilter}
                >
                  {savingFilter ? 'Saving…' : 'Save filter'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="workflow-filter-editor__conditions">
          <h6>Conditions</h6>
          {conditions.length === 0 && <p className="text-muted">No conditions yet. Use the form below to add one.</p>}
          {conditions.length > 0 && (
            <ul className="workflow-filter-editor__condition-list">
              {conditions.map((condition, index) => (
                <li key={`${condition.field}-${index}`}>
                  <div>
                    {index > 0 && (
                      <span className="workflow-filter-editor__condition-chip">{condition.condition?.toUpperCase()}</span>
                    )}
                    <strong>{fieldMap[condition.field]?.label || condition.field}</strong>
                    {' '}
                    <span>{operatorLabelMap[condition.operator] || condition.operator}</span>
                    {' '}
                    <strong>{condition.value}</strong>
                  </div>
                  <div className="workflow-filter-editor__condition-actions">
                    <button type="button" className="btn btn-link btn-xs" onClick={() => handleEditCondition(index)}>
                      Edit
                    </button>
                    <button type="button" className="btn btn-link btn-xs" onClick={() => handleDeleteCondition(index)}>
                      Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="workflow-filter-editor__builder">
          <h6>{editingConditionIndex !== null ? 'Update condition' : 'Add condition'}</h6>
          {conditions.length > 0 && (
            <div className="workflow-filter-editor__field">
              <label>Condition</label>
              <select
                className="form-control input-sm"
                value={conditionDraft.condition}
                onChange={(event) => updateConditionDraft({ condition: event.target.value })}
              >
                {conditionOptions.map((option) => (
                  <option key={option} value={option}>
                    {option.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="workflow-filter-editor__field">
            <label>Field</label>
            <select
              className="form-control input-sm"
              value={conditionDraft.field}
              onChange={(event) => updateConditionDraft({ field: event.target.value, field_index: event.target.selectedIndex - 1 })}
            >
              <option value="">Select field…</option>
              {fields.map((field) => (
                <option key={field.name} value={field.name}>
                  {field.label}
                </option>
              ))}
            </select>
          </div>
          <div className="workflow-filter-editor__field">
            <label>Operator</label>
            <select
              className="form-control input-sm"
              value={conditionDraft.operator}
              onChange={(event) => updateConditionDraft({ operator: event.target.value })}
            >
              {operators.map((operator) => (
                <option key={operator.value} value={operator.value}>
                  {operator.label}
                </option>
              ))}
            </select>
          </div>
          <div className="workflow-filter-editor__field">
            <label>Value</label>
            {renderConditionValueInput()}
          </div>
          <div className="workflow-filter-editor__builder-actions">
            {editingConditionIndex !== null && (
              <button type="button" className="btn btn-link btn-xs" onClick={resetConditionDraft}>
                Cancel
              </button>
            )}
            <button type="button" className="btn btn-primary btn-xs" onClick={addOrUpdateCondition}>
              {editingConditionIndex !== null ? 'Update condition' : 'Add condition'}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

WorkflowFilterEditor.propTypes = {
  open: PropTypes.bool.isRequired,
  workflowId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  selectedFilterTid: PropTypes.string,
  onPersistFilterSelection: PropTypes.func,
  persistingFilter: PropTypes.bool,
  onInfo: PropTypes.func,
  onError: PropTypes.func,
  onRefreshBoard: PropTypes.func,
};

WorkflowFilterEditor.defaultProps = {
  selectedFilterTid: null,
  onPersistFilterSelection: null,
  persistingFilter: false,
  onInfo: null,
  onError: null,
  onRefreshBoard: null,
};

export default WorkflowFilterEditor;
